import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatElapsed, GAME_PLAY_HINTS, GAME_RANKING_HINTS } from '@/utils/groupGameBareme';
import useGameTimer from '@/components/games/useGameTimer';
import SudokuBoard from '@/components/games/SudokuBoard';
import WordSearchBoard from '@/components/games/WordSearchBoard';
import DifferenceBoard from '@/components/games/DifferenceBoard';
import MemoryBoard from '@/components/games/MemoryBoard';
import PuzzleBoard from '@/components/games/PuzzleBoard';
import GameResultsPanel from '@/components/games/GameResultsPanel';

const RULES_OPEN_KEY = 'prysma_game_rules_open';

function readRulesOpen() {
  try {
    return sessionStorage.getItem(RULES_OPEN_KEY) !== '0';
  } catch {
    return true;
  }
}

export function GameHintBlock({ kind }) {
  const play = GAME_PLAY_HINTS[kind];
  const rank = GAME_RANKING_HINTS[kind];
  const [open, setOpen] = useState(readRulesOpen);

  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  if (!play && !rank?.length) return null;

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { sessionStorage.setItem(RULES_OPEN_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div className="text-xs leading-relaxed text-gray-700 dark:text-gray-200 bg-orange-50/80 dark:bg-orange-950/30 border border-orange-200/80 dark:border-orange-900/50 rounded-md px-3 py-2 mb-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="flex items-center gap-1.5 w-full text-left font-semibold text-gray-800 dark:text-gray-100"
      >
        {open ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
        Règles
      </button>
      {open ? (
        <div className="mt-1.5">
          {play ? <p>{play}</p> : null}
          {rank?.length ? (
            <div className={play ? 'mt-2' : ''}>
              <p className="font-semibold text-gray-800 dark:text-gray-100">Classement</p>
              <ol className="list-decimal list-inside mt-0.5 space-y-0.5">
                {rank.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default function GamePlayPanel({
  instanceId,
  token,
  meId,
  phase,
  showHint = true,
  onFinished,
  previewKind,
  previewPayload,
}) {
  const isPreview = Boolean(previewPayload);
  const timer = useGameTimer({ instanceId: isPreview ? undefined : instanceId, token: isPreview ? undefined : token });
  const [result, setResult] = useState(null);
  const [started, setStarted] = useState(false);
  const [previewProgress, setPreviewProgress] = useState({});
  const [previewElapsed, setPreviewElapsed] = useState(0);

  useEffect(() => {
    if (!isPreview) return undefined;
    setResult(null);
    setPreviewProgress({});
    setPreviewElapsed(0);
    setStarted(true);
    const startedAt = Date.now();
    const id = window.setInterval(() => setPreviewElapsed(Date.now() - startedAt), 200);
    return () => window.clearInterval(id);
  }, [isPreview, previewPayload]);

  useEffect(() => {
    if (isPreview || phase === 'results') return undefined;
    let cancelled = false;
    (async () => {
      const data = await timer.start();
      if (!cancelled) setStarted(true);
      if (data?.alreadyWon) setResult({ alreadyWon: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [isPreview, instanceId, phase, timer.start]);

  const onSolved = async () => {
    if (isPreview) {
      setResult({ ok: true, preview: true });
      return;
    }
    const data = await timer.complete();
    setResult(data || { ok: true });
  };

  const finished = !!(result?.ok || result?.alreadyWon);
  const payload = isPreview ? previewPayload : timer.payload;
  const kind = isPreview ? previewKind : timer.kind;
  const progress = isPreview ? previewProgress : timer.progress;
  const elapsedMs = isPreview ? previewElapsed : timer.elapsedMs;
  const saveProgress = isPreview
    ? (next) => setPreviewProgress(next || {})
    : (next) => timer.saveProgress(next);

  useEffect(() => {
    if (isPreview || !finished) return;
    onFinished?.();
  }, [isPreview, finished, onFinished]);

  if (!isPreview && onFinished && finished) return null;

  if (!isPreview && !onFinished && (phase === 'results' || (result?.alreadyWon && !timer.payload))) {
    return <GameResultsPanel instanceId={instanceId} token={token} meId={meId} />;
  }

  const board = payload && (
    kind === 'sudoku' ? (
      <SudokuBoard
        payload={payload}
        savedBoard={progress?.board}
        savedErrors={progress?.errors}
        savedErrorCells={progress?.errorCells}
        onSolved={onSolved}
        onProgress={saveProgress}
      />
    )
    : kind === 'mots_meles' ? (
      <WordSearchBoard
        payload={payload}
        savedFound={progress?.found}
        savedFoundCells={progress?.foundCells}
        savedBonusGuess={progress?.bonusGuess}
        savedBonusSolved={progress?.bonusSolved}
        onSolved={onSolved}
        onProgress={saveProgress}
      />
    )
    : kind === 'memory' ? (
      <MemoryBoard
        payload={payload}
        savedMatched={progress?.matched}
        savedMoves={progress?.moves}
        compact={finished}
        onSolved={onSolved}
        onProgress={saveProgress}
      />
    )
    : kind === 'difference' ? (
      <DifferenceBoard
        payload={payload}
        savedFound={progress?.found}
        onSolved={onSolved}
        onProgress={saveProgress}
      />
    )
    : kind === 'puzzle' ? (
      <PuzzleBoard
        payload={payload}
        savedSlots={progress?.slots}
        savedTray={progress?.tray}
        savedTrayPos={progress?.trayPos}
        onSolved={onSolved}
        onProgress={saveProgress}
      />
    )
    : null
  );

  return (
    <div className="mt-3 min-h-0">
      {showHint ? <GameHintBlock kind={kind} /> : null}
      <div className={kind === 'memory' ? 'mb-1.5' : 'mb-3'}>
        <p className="text-sm font-mono font-bold text-[#f76f4b]">{formatElapsed(elapsedMs)}</p>
        {kind === 'memory' && (
          <p className="text-xs font-semibold text-gray-600 dark:text-gray-300 mt-0.5">
            {Number(progress?.moves) || 0} coup{(Number(progress?.moves) || 0) !== 1 ? 's' : ''}
          </p>
        )}
        {timer.error && <p className="text-xs text-red-500 mt-1">{timer.error}</p>}
        {result?.ok && (
          <p className="text-xs text-green-600 mt-1">
            {result.preview
              ? 'Test terminé. Cette partie n’est pas enregistrée au classement.'
              : `Bravo, partie validée !${result?.rank != null ? ` Rang ${result.rank} · ${result.points || 0} pts` : ''}`}
          </p>
        )}
      </div>
      <div className="w-full min-w-0 overflow-visible">{started ? board : <p className="text-xs text-gray-400">Chargement…</p>}</div>
      {!isPreview && finished && !onFinished ? (
        <GameResultsPanel instanceId={instanceId} token={token} meId={meId} compact />
      ) : null}
    </div>
  );
}
